import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';

interface Category {
  name: string;
  description: string;
  icon: string;
  count: number;
}

const GENRE_META: Record<string, { description: string; icon: string }> = {
  'Psychological': {
    description: 'Mind-bending tales that blur reality and madness',
    icon: 'ri-brain-line',
  },
  'Supernatural': {
    description: 'Ghosts, demons, and things that go bump in the night',
    icon: 'ri-ghost-line',
  },
  'Cosmic Horror': {
    description: 'Existential horror from beyond comprehension',
    icon: 'ri-space-ship-line',
  },
  'Paranormal': {
    description: 'Modern myths and cursed tales from beyond',
    icon: 'ri-eye-2-line',
  },
  'Thriller': {
    description: 'Visceral survival horror and relentless pursuit',
    icon: 'ri-knife-line',
  },
  'Horror': {
    description: 'Grotesque transformations of flesh and form',
    icon: 'ri-virus-line',
  },
  'Gothic': {
    description: 'Dark, atmospheric tales of decay and dread',
    icon: 'ri-ancient-gate-line',
  },
  'Dark Fantasy': {
    description: 'Worlds where darkness and magic intertwine',
    icon: 'ri-sword-line',
  },
};

const CategoryGrid = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCounts = async () => {
      try {
        const { data, error } = await supabase
          .from('stories')
          .select('genre')
          .eq('status', 'published');

        if (error) throw error;

        const countMap: Record<string, number> = {};
        (data || []).forEach((row) => {
          if (row.genre) {
            countMap[row.genre] = (countMap[row.genre] || 0) + 1;
          }
        });

        const built: Category[] = Object.entries(GENRE_META).map(([name, meta]) => ({
          name,
          count: countMap[name] || 0,
          description: meta.description,
          icon: meta.icon,
        })).sort((a, b) => b.count - a.count);

        setCategories(built);
      } catch (err) {
        console.error('Error fetching genre counts:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchCounts();
  }, []);

  return (
    <section className="py-20 px-4 bg-[#0a0a0a]">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-red-600 mb-4 font-['Cinzel']">
            Explore by Genre
          </h2>
          <p className="text-gray-400 text-lg font-['Crimson_Text']">
            Choose your nightmare
          </p>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[1, 2, 3, 4].map((i) => (
              <div
                key={i}
                className="bg-gradient-to-br from-[#1a0a0a] to-[#0f0f0f] border border-red-900/20 rounded-lg p-6 animate-pulse"
              >
                <div className="w-16 h-16 bg-gray-800 rounded-lg mb-4" />
                <div className="h-5 bg-gray-800 rounded w-3/4 mb-2" />
                <div className="h-4 bg-gray-800 rounded mb-4" />
                <div className="h-4 bg-gray-800 rounded w-1/2" />
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {categories.map((category, index) => (
              <Link
                key={category.name}
                to={`/stories?category=${encodeURIComponent(category.name)}`}
                className="group relative bg-gradient-to-br from-[#1a0a0a] to-[#0f0f0f] border border-red-900/20 rounded-lg p-6 hover:border-red-600/50 transition-all duration-300 hover:shadow-2xl hover:shadow-red-900/30 cursor-pointer overflow-hidden"
                style={{ animation: `fadeInScale 0.5s ease-out ${index * 0.08}s both` }}
              >
                {/* Background glow */}
                <div className="absolute inset-0 opacity-5">
                  <div className="absolute inset-0 bg-gradient-to-br from-red-600 to-transparent" />
                </div>

                {/* Icon */}
                <div className="relative mb-4 w-16 h-16 flex items-center justify-center bg-red-600/10 rounded-lg group-hover:bg-red-600/20 transition-colors">
                  <i className={`${category.icon} text-4xl text-red-500 group-hover:scale-110 transition-transform`} />
                </div>

                {/* Content */}
                <h3 className="relative text-xl font-bold text-white mb-2 font-['Cinzel'] group-hover:text-red-500 transition-colors">
                  {category.name}
                </h3>

                <p className="relative text-gray-400 text-sm mb-4 font-['Crimson_Text'] leading-relaxed">
                  {category.description}
                </p>

                {/* Story Count */}
                <div className="relative flex items-center justify-between pt-4 border-t border-red-900/20">
                  <span className="text-gray-500 text-sm font-['Crimson_Text']">
                    {category.count.toLocaleString()} {category.count === 1 ? 'story' : 'stories'}
                  </span>
                  <i className="ri-arrow-right-line text-red-500 group-hover:translate-x-2 transition-transform" />
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>

      <style>{`
        @keyframes fadeInScale {
          from { opacity: 0; transform: scale(0.9); }
          to   { opacity: 1; transform: scale(1); }
        }
      `}</style>
    </section>
  );
};

export default CategoryGrid;
